import { useEffect, useState, useCallback } from "react";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import { formatDateTime } from "@/utils/format";
import { ScheduleModal } from "@/components/scheduling/ScheduleModal";

export function AppointmentsPage() {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const [appointments, setAppointments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  const facilityKey = getFacilityKeyForPortal();
  const patientKey = getPatientKey();
  const locationCode = facilityKey || String(currentPatient?.facilityID ?? "");

  const fetchAppointments = useCallback(() => {
    if (!currentPatient || !facilityKey || !patientKey) return;
    setLoading(true);
    patientRequest.request
      .getpatientfutureappts(facilityKey, patientKey)
      .then((res) => {
        const data = (res?.data as string[] | undefined) ?? [];
        const sorted = [...data].sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime(),
        );
        setAppointments(sorted);
      })
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  }, [currentPatient, facilityKey, patientKey]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleBooked = () => {
    fetchAppointments();
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div
              className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy"
            />
            <h2 className="text-xl font-extrabold text-healthsource-dark">
              Appointments
            </h2>
            {appointments.length > 0 && (
              <span
                className="ml-2.5 rounded-xl bg-[var(--hs-tint)] px-2.5 py-0.5 text-xs font-bold text-healthsource-navy"
              >
                {appointments.length}
              </span>
            )}
          </div>
          {locationCode && (
            <button
              onClick={() => setShowSchedule(true)}
              className="inline-flex min-h-11 items-center gap-2 rounded-md px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: "var(--hs-red)" }}
            >
              <i className="fas fa-calendar-plus text-sm" />
              Schedule
            </button>
          )}
        </div>

        <div
          className="sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]"
        >
          {loading && (
            <div className="py-6 text-center text-sm text-healthsource-gray">
              Loading appointments…
            </div>
          )}

          {!loading && appointments.length === 0 && (
            <div className="py-6 text-center text-sm text-healthsource-gray">
              No upcoming appointments.
            </div>
          )}

          {!loading && appointments.length > 0 && (
            <ul className="flex flex-col gap-3 sm:gap-3.5 sm:p-5">
              {appointments.map((dateStr, i) => (
                <li
                  key={i}
                  className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender bg-white transition-shadow hover:shadow-md"
                  style={{ boxShadow: "0 2px 8px rgba(30,37,43,0.06)" }}
                >
                  <div
                    className="w-1 flex-shrink-0 rounded-r-sm bg-healthsource-navy"
                  />
                  <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-4 sm:pl-5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-healthsource-navy/10">
                      <i className="far fa-calendar-alt text-lg text-healthsource-navy" aria-hidden />
                    </div>
                    <p className="text-[15px] font-extrabold text-healthsource-dark">
                      {formatDateTime(dateStr)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <ScheduleModal
        open={showSchedule}
        onClose={() => setShowSchedule(false)}
        locationCode={locationCode}
        onBooked={handleBooked}
      />
    </div>
  );
}
